import { Controller } from "@hotwired/stimulus"

// Sininho do header. Fonte da verdade = servidor (tabela notificacoes).
// Este controller NÃO monta HTML de notificação — os <li> vêm renderizados pelo
// servidor (no load) ou via Turbo Stream prepend (realtime). Aqui só:
//   - conta não-lidas a partir do DOM (badge, subtítulo, empty state, contadores de abas)
//   - filtra itens visualmente por categoria (Todas, Campanhas, Sistema, Alertas, Outras)
//   - marca como lida (fetch) e remove do DOM
//   - dispara o popup nativo do navegador quando chega item novo em tempo real
export default class extends Controller {
  static targets = [
    "badge",
    "lista",
    "subtitle",
    "emptyMsg",
    "emptyFilteredMsg",
    "filterBtn",
    "filterCount",
    "filterLabel"
  ]

  connect() {
    this.filtroAtivo = "todas"
    this.aplicarFiltro()
    this.sync()

    if (this.hasListaTarget) {
      // Itens do server-render já estão no DOM em connect(): não geram mutação,
      // logo não disparam popup. Só os que chegam depois (realtime) disparam.
      this.observer = new MutationObserver((muts) => this.onMutations(muts))
      this.observer.observe(this.listaTarget, { childList: true })
    }

    // Ouvinte para revalidar estado se preferência mudar em outro componente
    this.handlePrefChange = () => {
      this.aplicarFiltro()
      this.sync()
    }
    window.addEventListener("notif-pref-changed", this.handlePrefChange)
  }

  disconnect() {
    this.observer?.disconnect()
    window.removeEventListener("notif-pref-changed", this.handlePrefChange)
  }

  // ── Preferências de Categorias ──────────────────────────────────────────────
  get allowedCategories() {
    const raw = localStorage.getItem("notif_browser_categories")
    if (!raw) return ["campanha", "sistema", "warning", "error", "success", "info"]
    try {
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? parsed : ["campanha", "sistema", "warning", "error", "success", "info"]
    } catch {
      return ["campanha", "sistema", "warning", "error", "success", "info"]
    }
  }

  filtroVisivel(filtro) {
    const allowed = this.allowedCategories
    if (filtro === "todas") return true
    return allowed.includes(filtro)
  }

  normalizarTipo(tipo) {
    const t = (tipo || "sistema").toString().toLowerCase()
    if (t === "erro" || t === "danger") return "error"
    if (t === "alerta") return "warning"
    if (t === "sucesso") return "success"
    return t
  }

  tipoHabilitado(tipo) {
    return this.allowedCategories.includes(this.normalizarTipo(tipo))
  }

  // ── Contagem / UI ──────────────────────────────────────────────────────────
  get todosItens() {
    if (!this.hasListaTarget) return []
    const allUnread = Array.from(this.listaTarget.querySelectorAll(".notif-item.unread"))
    return allUnread.filter((li) => this.tipoHabilitado(li.dataset.notifTipo))
  }

  get itensFiltrados() {
    return this.todosItens.filter((li) =>
      this.correspondeFiltro(li.dataset.notifTipo, this.filtroAtivo)
    )
  }

  correspondeFiltro(tipo, filtro) {
    if (!filtro || filtro === "todas") return true
    return this.normalizarTipo(tipo) === filtro
  }

  filtrar(event) {
    const filtro = event.currentTarget.dataset.filter || "todas"
    this.filtroAtivo = filtro

    // Atualiza classe active nos botões de filtro
    if (this.hasFilterBtnTargets) {
      this.filterBtnTargets.forEach((btn) => {
        const isActive = (btn.dataset.filter || "todas") === filtro
        btn.classList.toggle("active", isActive)
        btn.setAttribute("aria-selected", isActive ? "true" : "false")
      })
    }

    this.aplicarFiltro()
    this.sync()
  }

  aplicarFiltro() {
    // Se o filtro atualmente selecionado foi desmarcado nas preferências, reseta para 'todas'
    if (!this.filtroVisivel(this.filtroAtivo)) {
      this.filtroAtivo = "todas"
    }

    const unread = this.todosItens

    // 1. Atualiza visibilidade dos <li> na lista
    if (this.hasListaTarget) {
      const allLi = this.listaTarget.querySelectorAll(".notif-item")
      allLi.forEach((li) => {
        const isEnabled = this.tipoHabilitado(li.dataset.notifTipo)
        const matches = this.correspondeFiltro(li.dataset.notifTipo, this.filtroAtivo)
        li.classList.toggle("d-none", !isEnabled || !matches)
      })
    }

    // 2. Atualiza visibilidade das opções no dropdown do menu hambúrguer
    if (this.hasFilterBtnTargets) {
      this.filterBtnTargets.forEach((btn) => {
        const f = btn.dataset.filter || "todas"
        const isVisible = this.filtroVisivel(f)
        btn.classList.toggle("d-none", !isVisible)

        const isActive = f === this.filtroAtivo
        btn.classList.toggle("active", isActive)
        btn.setAttribute("aria-selected", isActive ? "true" : "false")
      })
    }

    // 3. Atualiza contadores individuais de cada opção de filtro
    const contadores = {
      todas: unread.length,
      campanha: 0,
      sistema: 0,
      warning: 0,
      error: 0,
      success: 0,
      info: 0
    }

    unread.forEach((li) => {
      const t = this.normalizarTipo(li.dataset.notifTipo)
      if (contadores[t] !== undefined) {
        contadores[t]++
      }
    })

    if (this.hasFilterCountTargets) {
      this.filterCountTargets.forEach((el) => {
        const fType = el.dataset.filterType
        const count = contadores[fType] ?? 0
        el.textContent = count
        el.classList.toggle("d-none", count === 0)
      })
    }
  }

  sync() {
    const totalGeral = this.todosItens.length
    const totalFiltrado = this.itensFiltrados.length

    if (this.hasBadgeTarget) {
      this.badgeTarget.textContent = totalGeral
      this.badgeTarget.classList.toggle("d-none", totalGeral === 0)
    }

    if (this.hasSubtitleTarget) {
      if (totalGeral === 0) {
        this.subtitleTarget.textContent = "Nada de novo"
      } else if (this.filtroAtivo === "todas") {
        this.subtitleTarget.textContent = `${totalGeral} não lida(s)`
      } else {
        const label = this.nomeFiltro(this.filtroAtivo)
        this.subtitleTarget.textContent = `${totalFiltrado} em ${label} (${totalGeral} no total)`
      }
    }

    if (this.hasFilterLabelTarget) {
      this.filterLabelTarget.textContent = `Exibindo: ${this.nomeFiltro(this.filtroAtivo)}`
    }

    // Empty states
    if (this.hasEmptyMsgTarget) {
      // Exibe sem notificações global
      this.emptyMsgTarget.classList.toggle("d-none", totalGeral > 0)
    }

    if (this.hasEmptyFilteredMsgTarget) {
      // Exibe vazio específico do filtro quando há notificações gerais mas zero no filtro atual
      const mostrarVazioFiltro = totalGeral > 0 && totalFiltrado === 0
      this.emptyFilteredMsgTarget.classList.toggle("d-none", !mostrarVazioFiltro)
    }
  }

  nomeFiltro(filtro) {
    switch (filtro) {
      case "campanha": return "Campanhas"
      case "sistema":  return "Sistema & Manutenções"
      case "warning":  return "Alertas & Pendências"
      case "error":    return "Erros & Falhas"
      case "success":  return "Sucessos & Confirmações"
      case "info":     return "Informativos Gerais"
      default:         return "Todas"
    }
  }

  onMutations(mutations) {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType === 1 && node.classList?.contains("notif-item")) {
          this.popupNavegador(node)
        }
      }
    }
    this.aplicarFiltro()
    this.sync()
  }

  // ── Ações ───────────────────────────────────────────────────────────────────
  abrir(event) {
    if (event.target.closest("a")) return // <a> do título navega sozinho
    if (event.target.closest(".notif-close")) return

    const url = event.currentTarget.dataset.notifUrl
    if (url) {
      window.Turbo ? window.Turbo.visit(url) : (window.location.href = url)
    }
  }

  dispensar(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dispensarItem(event.currentTarget.closest(".notif-item"))
  }

  dispensarItem(li) {
    if (!li) return
    const id = li.dataset.notifId
    li.classList.remove("unread")
    if (id) this.request(`/notificacoes/${id}/marcar_lida`, "PATCH")
    li.remove()
    this.aplicarFiltro()
    this.sync()
  }

  marcarTodasLidas() {
    this.request("/notificacoes/marcar_todas_lidas", "POST")
    if (this.hasListaTarget) {
      this.listaTarget.innerHTML = ""
    }
    this.aplicarFiltro()
    this.sync()
  }

  // ── Popup nativo (Web Notifications API) ─────────────────────────────────────
  popupNavegador(li) {
    if (li.dataset.notifBrowser === "false") return
    if (!this.browserHabilitado()) return

    const tipo = li.dataset.notifTipo || "sistema"
    if (!this.tipoPermitido(tipo)) return

    const titulo = li.dataset.notifTitulo ||
                   li.querySelector(".msg-a")?.textContent?.trim() ||
                   "Nova notificação"
    const mensagem = li.dataset.notifMensagem ||
                     li.querySelector(".notif-message")?.textContent?.trim() ||
                     ""
    const url = li.dataset.notifUrl

    try {
      const notif = new Notification(titulo, {
        body: mensagem,
        icon: "/favicon-32x32.png",
        badge: "/favicon-16x16.png",
        tag: `notif-${li.dataset.notifId || Date.now()}`,
        renotify: true
      })

      notif.onclick = (e) => {
        e.preventDefault()
        window.focus()
        if (url) {
          if (window.Turbo) {
            window.Turbo.visit(url)
          } else {
            window.location.href = url
          }
        }
        notif.close()
      }
    } catch {
      /* ignora falha de Notification */
    }
  }

  browserHabilitado() {
    return (
      "Notification" in window &&
      Notification.permission === "granted" &&
      localStorage.getItem("notif_browser_enabled") === "1"
    )
  }

  tipoPermitido(tipo) {
    let raw = localStorage.getItem("notif_browser_categories")
    if (!raw) return true // Por padrão, todos os tipos são permitidos

    try {
      const allowed = JSON.parse(raw)
      if (!Array.isArray(allowed)) return true

      // Normaliza variações de nomes
      let t = (tipo || "sistema").toLowerCase()
      if (t === "erro") t = "error"
      if (t === "alerta") t = "warning"
      if (t === "sucesso") t = "success"

      return allowed.includes(t)
    } catch {
      return true
    }
  }

  // ── HTTP ─────────────────────────────────────────────────────────────────────
  request(url, method) {
    fetch(url, {
      method,
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
        "Accept": "application/json"
      }
    }).catch(() => { /* offline: badge/DOM já atualizou localmente */ })
  }
}
